import React from "react";
import { Person, ProgramTypeName, ProgramStatus, Classification } from "@/types";
import { capitalize } from "lodash";
import { getProgramStatus, getPrimarySectionName } from "@/pages/people/_utils";
import { isInFuture, toFormat } from "@/lib/dateHelpers";
import {
  isNotTerminatedMemberOrAssociate,
  hasEmploymentEnded,
  getCurrentPrimaryEmployment,
  shouldSuppressUnemployedForClassification,
  getDisplayedCcmsSuspension,
  getSuspensionReasonLabel
} from "../../utils/utils";
import Tag from "@/components/Tag";
import { TagType, TagStyle } from "@/components/Tag/Tag";
import Tooltip from "@/components/Tooltip";
import { m360Paths } from "@/lib/m360Paths";

export type HeadingMetaItem = {
  text: string;
  boldValue?: boolean;
  href?: string;
  label?: string;
  tooltipText?: string;
};

export const NO_PRIMARY_EMPLOYMENT_ON_RECORD = "No primary employment on record";

export const shouldShowPersonHeadingEmployment = (person: Person): boolean => {
  if (person.latestProgram?.programType.name === ProgramTypeName.Student) return false;
  if (person.latestProgram?.status === ProgramStatus.TERMINATED) return false;
  return true;
};

export const shouldShowPersonHeadingEmploymentEmptyState = (person: Person): boolean => {
  if (!shouldShowPersonHeadingEmployment(person)) return false;
  if (!isNotTerminatedMemberOrAssociate(person)) return false;
  if (getCurrentPrimaryEmployment(person.employments)) return false;
  if (shouldSuppressUnemployedForClassification(person)) return false;
  return true;
};

export const getMostRecentEndedEmploymentTooltip = (person: Person): string | undefined => {
  const mostRecentEndedEmployment = [...(person.employments ?? [])]
    .filter((employment) => hasEmploymentEnded(employment))
    .sort((a, b) => {
      if (!a.endedOn || !b.endedOn) return 0;
      return new Date(b.endedOn).getTime() - new Date(a.endedOn).getTime();
    })[0];

  return mostRecentEndedEmployment?.endedOn
    ? `Employment Ended ${toFormat(mostRecentEndedEmployment.endedOn)}`
    : undefined;
};

export const createProgramAndSectionInfo = (
  person: Person,
  pgaId: string,
  linkIdentityChrome = false
): HeadingMetaItem[] => {
  const { latestProgram } = person;
  const primarySectionName = getPrimarySectionName(person);

  if (!latestProgram) {
    return primarySectionName ? [{ text: primarySectionName, boldValue: true }] : [];
  }

  const { programType, band } = latestProgram;

  const programText = programType.name;

  let combinedText: string = programText;

  if (programType.name === ProgramTypeName.Student) {
    const universityName = band?.section?.name;
    combinedText = universityName ? `${programText}, ${universityName}` : programText;
  } else if (programType.name !== ProgramTypeName.Prospect && primarySectionName) {
    combinedText = `${programText}, ${primarySectionName}`;
  }

  return [
    {
      text: combinedText,
      boldValue: true,
      href: linkIdentityChrome ? m360Paths.personMembership(pgaId) : undefined
    }
  ];
};

export const createClassificationInfo = (
  person: Person,
  pgaId: string,
  classifications?: Classification[],
  linkIdentityChrome = false,
  refreshEmployment = false
): HeadingMetaItem[] => {
  const { latestProgram, employments } = person;

  if (latestProgram?.programType.name === ProgramTypeName.Student) {
    return [];
  }

  const primaryEmployment = refreshEmployment
    ? getCurrentPrimaryEmployment(employments)
    : employments?.find((employment) => employment.isPrimary);
  const showJob = refreshEmployment
    ? Boolean(primaryEmployment && shouldShowPersonHeadingEmployment(person))
    : Boolean(primaryEmployment && !primaryEmployment.endedOn);

  if (showJob && primaryEmployment) {
    const matchingClassification = classifications?.find(
      (classification) => classification.name === primaryEmployment.jobClassification
    );

    const displayTitle = primaryEmployment.formattedTitle || primaryEmployment.title;
    const jobClassification = primaryEmployment.jobClassification;
    const classificationDescription = matchingClassification?.description?.trim();

    if (!displayTitle || !jobClassification) {
      return [];
    }

    return [
      {
        text: `${displayTitle} (${jobClassification})`,
        label: "Job",
        tooltipText: classificationDescription || undefined,
        href: linkIdentityChrome ? m360Paths.personCareer(pgaId) : undefined
      }
    ];
  }

  const hasActiveBand =
    latestProgram?.band?.classification && latestProgram?.band?.expiresOn && isInFuture(latestProgram.band.expiresOn);

  if (hasActiveBand && latestProgram.band) {
    const { band, programType } = latestProgram;
    const { name, description } = band.classification;

    return [
      {
        text: `${name} (${programType.name})`,
        label: "Class",
        tooltipText: description
      }
    ];
  }

  return [];
};

export const createFacilityInfo = (
  person: Person,
  linkIdentityChrome = false,
  refreshEmployment = false
): HeadingMetaItem[] => {
  if (refreshEmployment) {
    if (!shouldShowPersonHeadingEmployment(person)) {
      return [];
    }
  } else if (person.latestProgram?.programType.name === ProgramTypeName.Student) {
    return [];
  }

  const primaryEmployment = refreshEmployment
    ? getCurrentPrimaryEmployment(person.employments)
    : person.employments?.find((employment) => employment.isPrimary);

  if (!primaryEmployment) {
    if (isNotTerminatedMemberOrAssociate(person)) {
      if (shouldSuppressUnemployedForClassification(person)) {
        return [];
      }

      return [
        {
          text: "Unemployed",
          label: "Facility",
          tooltipText: getMostRecentEndedEmploymentTooltip(person)
        }
      ];
    }
    return [];
  }

  if (!primaryEmployment.facility) return [];

  const { facility } = primaryEmployment;
  const facilityName = facility.name;
  const facilityHref = linkIdentityChrome && facility.id ? m360Paths.facility(facility.id) : undefined;

  const mainAddress = facility.addresses?.find((addr) => addr.addressType === "main") || facility.addresses?.[0];

  let facilityText = facilityName;
  if (mainAddress?.city && mainAddress?.state) {
    facilityText = `${facilityName}, ${mainAddress.city}, ${mainAddress.state}`;
  }

  return [{ text: facilityText, label: "Facility", href: facilityHref }];
};

export const hasActiveClassFBand = (person: Person): boolean => {
  const { latestProgram } = person;
  const band = latestProgram?.band;

  return !!(
    band?.classification?.name === "F" &&
    (latestProgram?.status.includes("ACTIVE") || latestProgram?.status.includes("SUSPENDED"))
  );
};

export const ProgramStatusTag = ({
  status,
  terminatedReason,
  terminatesOn,
  suspensionReason,
  suspensionStartDate,
  pendingTerminatesOn,
  size = "small",
  compact = false
}: {
  status: string;
  terminatedReason?: string | null;
  terminatesOn?: string | null;
  suspensionReason?: string | null;
  suspensionStartDate?: string | null;
  pendingTerminatesOn?: string | null;
  size?: "small" | "medium" | "large";
  compact?: boolean;
}) => {
  const isDeceased = terminatedReason?.toLowerCase() === "deceased";
  const isSuspended = status === "Suspended";
  const isTerminated = status === "Terminated" && !isDeceased;
  const isPending = status === "Pending";

  const statusConfig: Record<string, { tagType: TagType; tagStyle: TagStyle }> = {
    Active: {
      tagType: "success",
      tagStyle: "strong"
    },
    Pending: {
      tagType: "warn",
      tagStyle: "strong"
    },
    Inactive: { tagType: "surface", tagStyle: "strong" },
    Suspended: { tagType: "warn", tagStyle: "strong" },
    Terminated: isDeceased ? { tagType: "surface", tagStyle: "strong" } : { tagType: "error", tagStyle: "strong" }
  };

  const config = statusConfig[status as keyof typeof statusConfig];
  if (!config) return null;

  const displayText = isDeceased ? "Deceased" : capitalize(status);

  const formatReasonWithDate = (reason: string, date?: string | null) =>
    date ? `${reason} on ${toFormat(date, "MMM dd, yyyy")}` : reason;

  const tooltipText =
    isDeceased && terminatesOn
      ? formatReasonWithDate("Deceased", terminatesOn)
      : isPending && pendingTerminatesOn
        ? formatReasonWithDate("Pending registration terminates", pendingTerminatesOn)
        : isSuspended && suspensionReason
          ? formatReasonWithDate(suspensionReason, suspensionStartDate)
          : isTerminated && terminatedReason
            ? formatReasonWithDate(capitalize(terminatedReason.replace(/_/g, " ").toLowerCase()), terminatesOn)
            : undefined;

  const iconName = isDeceased
    ? "info"
    : isPending && tooltipText
      ? "info"
      : (isSuspended || isTerminated) && tooltipText
        ? "error"
        : undefined;

  if (tooltipText) {
    return (
      <Tooltip
        variant="plain"
        supportingText={tooltipText}
        color="black"
        menuCorner="end-start"
        anchorCorner="start-start"
        positioning="popover"
        anchorClassName="p-1 -m-1"
      >
        <Tag tagType={config.tagType} tagStyle={config.tagStyle} size={size} compact={compact} iconName={iconName}>
          {displayText}
        </Tag>
      </Tooltip>
    );
  }

  return (
    <Tag tagType={config.tagType} tagStyle={config.tagStyle} size={size} compact={compact} iconName={iconName}>
      {displayText}
    </Tag>
  );
};

export const CLASS_F_TAG_TOOLTIP = "Didn't meet last cycle's PDR requirements";

export const ClassFTag = ({
  size = "small",
  compact = false
}: {
  size?: "small" | "medium" | "large";
  compact?: boolean;
}) => {
  return (
    <Tooltip
      variant="plain"
      supportingText={CLASS_F_TAG_TOOLTIP}
      color="black"
      menuCorner="end-start"
      anchorCorner="start-start"
      positioning="popover"
      anchorClassName="p-1 -m-1"
    >
      <Tag tagType="error" size={size} compact={compact} iconName="info">
        Class F
      </Tag>
    </Tooltip>
  );
};

export const buildPersonTags = (person: Person, hideTags: boolean): React.ReactNode[] | undefined => {
  if (hideTags) return;

  const programStatus = getProgramStatus(person);
  const activeSuspension = getDisplayedCcmsSuspension(programStatus, person.ccmsSuspensions);
  const suspensionReason = activeSuspension ? getSuspensionReasonLabel(activeSuspension) : undefined;
  const tags: React.ReactNode[] = [];

  if (programStatus) {
    tags.push(
      <ProgramStatusTag
        key="status"
        status={programStatus}
        terminatedReason={person.latestProgram?.band?.terminatedReason}
        terminatesOn={person.latestProgram?.band?.terminatesOn}
        suspensionReason={suspensionReason}
        suspensionStartDate={activeSuspension?.startDate}
        pendingTerminatesOn={person.latestProgram?.band?.expiresOn}
      />
    );
  }

  if (hasActiveClassFBand(person)) {
    tags.push(<ClassFTag key="class-f" />);
  }

  return tags.length ? tags : undefined;
};
